import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { vatAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const VatSettingForm = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();

  // Prevent scroll wheel from changing number input values
  const handleNumberInputWheel = (e) => {
    e.currentTarget.blur();
  };
  const [vat, setVat] = useState({
    id: null,
    name: 'VAT',
    percentage: 0,
    description: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allVats, setAllVats] = useState([]);
  const [showAllVats, setShowAllVats] = useState(false);

  useEffect(() => {
    loadVat();
  }, []);

  const loadVat = async () => {
    try {
      setLoading(true);
      const response = await vatAPI.get();
      const data = response.data.data;
      setVat({
        id: data.id,
        name: data.name || 'VAT',
        percentage: data.percentage || 0,
        description: data.description || '',
        is_active: data.is_active ?? true,
      });
    } catch (error) {
      console.error('Error loading VAT:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllVats = async () => {
    try {
      const response = await vatAPI.getAll();
      setAllVats(response.data.data || []);
    } catch (error) {
      console.error('Error loading VAT history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (vat.id) {
        await vatAPI.update(vat.id, {
          name: vat.name,
          percentage: parseFloat(vat.percentage),
          description: vat.description,
          is_active: vat.is_active,
        });
      } else {
        await vatAPI.create({
          name: vat.name,
          percentage: parseFloat(vat.percentage),
          description: vat.description,
          is_active: true,
        });
      }
      await loadVat();
      showSuccess(t('common.success'));
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (vatId) => {
    try {
      await vatAPI.setActive(vatId);
      await loadVat();
      loadAllVats();
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-200 border-t-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* VAT Settings Form */}
      <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-yellow-500">
        <div className="flex items-center gap-3 mb-6">
          <svg className="h-6 w-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267.22-.07.466-.106.714-.106.748 0 1.454.191 2.027.567a4.902 4.902 0 011.524 1.465c.636.857.905 1.965.905 3.326 0 1.035-.181 1.922-.544 2.659-.363.737-.872 1.289-1.527 1.657-.653.368-1.43.552-2.331.552-.89 0-1.666-.184-2.331-.552-.656-.368-1.165-.92-1.527-1.657-.363-.737-.544-1.624-.544-2.659 0-1.36.268-2.469.905-3.326a4.884 4.884 0 011.524-1.465c.573-.376 1.279-.567 2.027-.567.248 0 .494.036.714.106.22.07.411.164.567.267zM10 14a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('vat.settings')}</h2>
            <p className="text-sm text-gray-600">{t('vat.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* VAT Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('vat.name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={vat.name}
              onChange={(e) => setVat({ ...vat, name: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
              placeholder="e.g., VAT"
            />
          </div>

          {/* VAT Percentage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('vat.percentage')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={vat.percentage}
                  onChange={(e) => setVat({ ...vat, percentage: e.target.value })}
                  onWheel={handleNumberInputWheel}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  placeholder="0.00"
                />
                <span className="absolute right-4 top-3 text-gray-500 font-medium">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('vat.example')}
              </p>
            </div>

            {/* VAT Preview */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('vat.preview')}
              </label>
              <div className="px-4 py-3 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">{t('vat.currentVAT')}</span> {parseFloat(vat.percentage).toFixed(2)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('vat.exampleOn')} <span className="font-semibold text-yellow-700">{(1000 * parseFloat(vat.percentage) / 100).toFixed(2)} SAR</span>
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('vat.descriptionLabel')}
            </label>
            <textarea
              value={vat.description}
              onChange={(e) => setVat({ ...vat, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
              placeholder="Optional description..."
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <input
              type="checkbox"
              id="is_active"
              checked={vat.is_active}
              onChange={(e) => setVat({ ...vat, is_active: e.target.checked })}
              className="h-5 w-5 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="is_active" className="block text-sm font-medium text-gray-700 cursor-pointer">
              {t('vat.active')}
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 transition-all font-bold shadow-md hover:shadow-lg"
            >
              {saving ? t('vat.saving') : t('vat.saveSettings')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAllVats(!showAllVats);
                if (!showAllVats) loadAllVats();
              }}
              className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all font-bold"
            >
              {showAllVats ? t('vat.hideHistory') : t('vat.viewHistory')}
            </button>
          </div>
        </form>
      </div>

      {/* VAT History */}
      {showAllVats && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <svg className="h-6 w-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H3a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V6a1 1 0 00-1-1h-3a1 1 0 000-2h2a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V5z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-bold text-gray-900">{t('vat.history')}</h3>
          </div>

          {allVats.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{t('vat.noRecords')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-900 to-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase">{t('vat.name_header')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase">{t('vat.percentage_header')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase">{t('vat.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase">{t('vat.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {allVats.map((v) => (
                    <tr key={v.id} className="hover:bg-yellow-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{v.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                        {parseFloat(v.percentage).toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {v.is_active ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                            {t('common.active')}
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold">
                            {t('common.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {!v.is_active && (
                          <button
                            onClick={() => handleSetActive(v.id)}
                            className="text-yellow-600 hover:text-yellow-900 font-semibold transition-colors"
                          >
                            {t('vat.setActive')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VatSettingForm;
